import Swup from 'swup';
import SwupJsPlugin from '@swup/js-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupScriptsPlugin from '@swup/scripts-plugin';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { lenisManager } from './LenisManager.js';

gsap.registerPlugin(ScrollTrigger);

class SwupManager {
  constructor() {
    this.swup = null;
    this.isInitialized = false;
    this.pageTransitionDuration = 0.6;
    this.skipAnimation = false; 
    this.transitionStyle = window.themeSettings?.pageTransitionStyle || 'slide';
  }

  init() {
    if (this.isInitialized) return this.swup;

    if (window.Shopify && window.Shopify.designMode) {
      return null;
    }

    const animationOptions = [
      {
        from: '(.*)', 
        to: '(.*)', 
        out: async (done) => {
          await this.animateOut();
          done();
        },
        in: async (done) => {
          await this.animateIn();
          done();
        },
      },
    ];

    this.swup = new Swup({
      containers: ['#swup-container'],
      cache: true,
      animateHistoryBrowsing: true,
      linkSelector:
        'a[href^="' +
        window.location.origin +
        '"]:not([data-no-swup]), a[href^="/"]:not([data-no-swup]):not([href^="//"]), a[href^="#"]:not([data-no-swup])',
      plugins: [
        new SwupJsPlugin(animationOptions),
        new SwupHeadPlugin({
          persistAssets: true,
          persistTags: 'link[rel="stylesheet"], style, script[src]',
        }),
        new SwupPreloadPlugin({
          preloadVisibleLinks: true,
        }),
        new SwupBodyClassPlugin(),
        new SwupScriptsPlugin({
          head: false,
          body: true,
        }),
      ],
    });

    this.setupEventListeners();

    this.isInitialized = true;

    return this.swup;
  }

  setupEventListeners() {
    this.swup.hooks.on('visit:start', (visit) => {
      document.documentElement.classList.add('is-changing');
      document.documentElement.classList.add('scroll-locked');

      lenisManager.stop();

      if (visit.to.url.includes('/cart')) {
        this.swup.cache.delete(visit.to.url);
      }
    });

    
    this.swup.hooks.on('content:replace', () => {
      
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

      
      lenisManager.scrollTo(0, { immediate: true });

      
      this.reinitializeComponents();

      
      
      const main = document.querySelector('main');
      if (main) {
        
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });

        
        this.announcePageChange();
      }

      
      requestAnimationFrame(() => {
        
        lenisManager.resize();

        
        ScrollTrigger.refresh();
      });
    });

    
    this.swup.hooks.on('visit:end', () => {
      document.documentElement.classList.remove('is-changing');
      document.documentElement.classList.remove('scroll-locked');
      lenisManager.start();

      
      window.dispatchEvent(new CustomEvent('swup:transitionEnd'));
    });

    
    this.swup.hooks.on('page:view', () => {
      
      window.dispatchEvent(
        new CustomEvent('swup:pageview', {
          detail: { url: window.location.href },
        }),
      );
    });
  }

  
  async animateOut() {
    
    if (this.skipAnimation) {
      return Promise.resolve();
    }

    const container = document.querySelector('#swup-container');
    const overlay = document.querySelector('.page-transition-overlay');

    const tl = gsap.timeline();

    switch (this.transitionStyle) {
      case 'fade':
        
        tl.to(container, {
          opacity: 0,
          duration: 0.35,
          ease: 'power2.inOut',
        });
        break;

      case 'slide':
        
        tl.to(container, {
          y: -40,
          opacity: 0,
          duration: 0.25,
          ease: 'power2.in',
        });
        break;

      case 'curtain':
      default:
        
        if (overlay) {
          gsap.set(overlay, { transformOrigin: 'bottom', scaleY: 0 });
          tl.to(overlay, {
            scaleY: 1,
            duration: 0.4,
            ease: 'power3.inOut',
          });
        }
        tl.to(
          container,
          {
            y: -40,
            opacity: 0,
            duration: 0.25,
            ease: 'power2.in',
          },
          0.1,
        );
        break;
    }

    return tl;
  }

  
  async animateIn() {
    const container = document.querySelector('#swup-container');
    const overlay = document.querySelector('.page-transition-overlay');

    
    if (this.skipAnimation) {
      
      this.skipAnimation = false;
      
      gsap.set(container, { opacity: 1, y: 0, x: 0 });
      if (overlay) gsap.set(overlay, { scaleY: 0, scaleX: 0 });
      return Promise.resolve();
    }

    const tl = gsap.timeline();

    switch (this.transitionStyle) {
      case 'fade':
        
        gsap.set(container, { opacity: 0, x: 0, y: 0 });
        tl.to(container, {
          opacity: 1,
          duration: 0.35,
          ease: 'power2.inOut',
        });
        tl.add(() => {
          this.animateRevealElements();
        }, 0.1);
        break;

      case 'slide':
        
        gsap.set(container, { opacity: 0, y: 40, x: 0 });
        tl.to(container, {
          y: 0,
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out',
        });
        tl.add(() => {
          this.animateRevealElements();
        }, 0.1);
        break;

      case 'curtain':
      default:
        
        gsap.set(container, { opacity: 0, x: 0, y: 40 });
        if (overlay) {
          gsap.set(overlay, { transformOrigin: 'top', scaleY: 1 });
          tl.to(overlay, {
            scaleY: 0,
            duration: 0.5,
            ease: 'power3.inOut',
          });
        }
        tl.to(
          container,
          {
            y: 0,
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
          },
          0.2,
        );
        tl.add(() => {
          this.animateRevealElements();
        }, 0.3);
        break;
    }

    return tl;
  }

  
  animateRevealElements() {
    const reveals = document.querySelectorAll('[data-reveal]');

    reveals.forEach((el, index) => {
      const delay = el.dataset.revealDelay || index * 0.1;
      const type = el.dataset.reveal || 'fade-up';

      gsap.to(el, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        clipPath: 'inset(0 0% 0 0)',
        duration: 0.8,
        delay: delay,
        ease: 'power2.out',
      });
    });
  }

  
  announcePageChange() {
    
    let liveRegion = document.getElementById('swup-announce');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'swup-announce';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }

    
    const pageTitle = document.title || 'Page loaded';
    liveRegion.textContent = '';
    
    setTimeout(() => {
      liveRegion.textContent = pageTitle;
    }, 100);
  }

  
  reinitializeComponents() {
    
    window.dispatchEvent(new CustomEvent('swup:contentReplaced'));

    
    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    
    document.querySelectorAll('details-disclosure, details-modal').forEach((el) => {
      if (el.connectedCallback) {
        el.connectedCallback();
      }
    });
  }

  
  navigateTo(url, skipAnimation = false) {
    if (this.swup) {
      this.skipAnimation = skipAnimation;
      this.swup.navigate(url);
    } else {
      window.location.href = url;
    }
  }

  
  updateUrl(url, replace = false) {
    if (replace) {
      window.history.replaceState({ swupPreview: true }, '', url);
    } else {
      window.history.pushState({ swupPreview: true }, '', url);
    }
  }

  
  goBack() {
    window.history.back();
  }

  
  clearCache(patterns) {
    if (!this.swup || !this.swup.cache) return;

    const patternsArray = Array.isArray(patterns) ? patterns : [patterns];

    
    this.swup.cache.all.forEach((page, url) => {
      if (patternsArray.some((pattern) => url.includes(pattern))) {
        this.swup.cache.delete(url);
      }
    });
  }

  
  clearAllCache() {
    if (this.swup && this.swup.cache) {
      this.swup.cache.clear();
    }
  }

  
  destroy() {
    if (this.swup) {
      this.swup.destroy();
      this.swup = null;
      this.isInitialized = false;
    }
  }
}

export const swupManager = new SwupManager();
export default swupManager;
